/**
 * Word splitting utility for clickable text rendering.
 *
 * Problem: The dictionary does not contain proper-noun possessives like
 * "Ando's", "Britain's", "Musk's" — so tapping them gives 404 to learners.
 *
 * Solution: Split the possessive clitic "'s" off the noun before lookup.
 * The noun becomes clickable on its own; the "'s" renders as plain text.
 *
 * We keep pronoun contractions intact ("it's", "he's", "she's" etc.) because
 * those are legitimate dictionary entries and carry their own meaning
 * (= it is / it has).
 */

/** Pronouns / function words whose "'s" forms are real contractions, not possessives. */
const CONTRACTION_BASES = new Set([
    'it', 'he', 'she', 'that', 'what', 'who', 'where', 'there',
    'here', 'let', 'how', 'when', 'why', 'one', 'someone', 'everyone',
    'nobody', 'everybody', 'something', 'nothing', 'everything',
]);

export interface SplitWord {
    /** The lookup word — what gets sent to the dictionary API */
    base: string;
    /** The plain-text tail that renders after the clickable word (e.g. "'s", or "") */
    tail: string;
}

/**
 * Split a tokenized word into its clickable base + trailing possessive "'s".
 *
 *   "Ando's"   → { base: "Ando",   tail: "'s" }
 *   "Britain's"→ { base: "Britain", tail: "'s" }
 *   "it's"     → { base: "it's",   tail: "" }     // contraction, keep intact
 *   "don't"    → { base: "don't",  tail: "" }
 *   "John"     → { base: "John",   tail: "" }
 *   "workers'" → { base: "workers'", tail: "" }   // trailing bare ' is excluded by \b in the regex upstream; defensive no-op here
 */
export function splitPossessive(word: string): SplitWord {
    const m = word.match(/^(.+?)('s)$/i);
    if (!m) return { base: word, tail: '' };
    const base = m[1];
    if (CONTRACTION_BASES.has(base.toLowerCase())) {
        return { base: word, tail: '' };
    }
    return { base, tail: m[2] };
}
